import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { ThumbsUp, ThumbsDown, MessageSquare, TrendingUp, Sparkles, Filter, X } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface FeedbackStat {
  id: string;
  feedback: 'positive' | 'negative';
  created_at: string;
  message_content: string;
  user_id: string;
  user_name: string;
  user_email: string;
}

interface FeedbackSummary {
  total: number;
  positive: number;
  negative: number;
  positiveRate: number;
}

interface UserOption {
  id: string;
  name: string;
  email: string;
}

export function AIFeedbackStats() {
  const [summary, setSummary] = useState<FeedbackSummary>({ total: 0, positive: 0, negative: 0, positiveRate: 0 });
  const [allFeedback, setAllFeedback] = useState<FeedbackStat[]>([]);
  const [filteredFeedback, setFilteredFeedback] = useState<FeedbackStat[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState<UserOption[]>([]);
  
  // Filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('all');
  const [feedbackType, setFeedbackType] = useState<string>('all');

  useEffect(() => {
    fetchFeedbackStats();
  }, []);

  // Apply filters whenever filter values change
  useEffect(() => {
    applyFilters();
  }, [dateFrom, dateTo, selectedUser, feedbackType, allFeedback]);

  const applyFilters = useCallback(() => {
    let filtered = [...allFeedback];

    // Filter by date from
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(f => new Date(f.created_at) >= fromDate);
    }

    // Filter by date to
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(f => new Date(f.created_at) <= toDate);
    }

    // Filter by user
    if (selectedUser && selectedUser !== 'all') {
      filtered = filtered.filter(f => f.user_id === selectedUser);
    }

    // Filter by feedback type
    if (feedbackType && feedbackType !== 'all') {
      filtered = filtered.filter(f => f.feedback === feedbackType);
    }

    setFilteredFeedback(filtered);

    // Update summary based on filtered data
    const total = filtered.length;
    const positive = filtered.filter(f => f.feedback === 'positive').length;
    const negative = total - positive;
    const positiveRate = total > 0 ? (positive / total) * 100 : 0;
    setSummary({ total, positive, negative, positiveRate });
  }, [dateFrom, dateTo, selectedUser, feedbackType, allFeedback]);

  const fetchFeedbackStats = async () => {
    setIsLoading(true);
    try {
      // Fetch all feedback
      const { data: feedbackData, error } = await supabase
        .from('chat_feedback')
        .select('id, feedback, created_at, message_id, user_id')
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (feedbackData && feedbackData.length > 0) {
        // Get all message contents
        const messageIds = feedbackData.map(f => f.message_id);
        const { data: messages } = await supabase
          .from('chat_messages')
          .select('id, content')
          .in('id', messageIds);

        // Get all user profiles
        const userIds = [...new Set(feedbackData.map(f => f.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, name, email')
          .in('id', userIds);

        const messagesMap = (messages || []).reduce((acc, m) => {
          acc[m.id] = m.content;
          return acc;
        }, {} as Record<string, string>);

        const profilesMap = (profiles || []).reduce((acc, p) => {
          acc[p.id] = { name: p.name, email: p.email };
          return acc;
        }, {} as Record<string, { name: string; email: string }>);

        // Set users for filter dropdown
        setUsers((profiles || []).map(p => ({ id: p.id, name: p.name, email: p.email })));

        const enrichedFeedback: FeedbackStat[] = feedbackData.map(f => ({
          id: f.id,
          feedback: f.feedback as 'positive' | 'negative',
          created_at: f.created_at,
          message_content: messagesMap[f.message_id] || 'Mensaje no disponible',
          user_id: f.user_id,
          user_name: profilesMap[f.user_id]?.name || 'Usuario',
          user_email: profilesMap[f.user_id]?.email || '',
        }));

        setAllFeedback(enrichedFeedback);
        setFilteredFeedback(enrichedFeedback);

        // Calculate initial summary
        const total = enrichedFeedback.length;
        const positive = enrichedFeedback.filter(f => f.feedback === 'positive').length;
        const negative = total - positive;
        const positiveRate = total > 0 ? (positive / total) * 100 : 0;
        setSummary({ total, positive, negative, positiveRate });
      }
    } catch (error) {
      console.error('Error fetching feedback stats:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setSelectedUser('all');
    setFeedbackType('all');
  };

  const hasActiveFilters = dateFrom || dateTo || selectedUser !== 'all' || feedbackType !== 'all';

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-primary/10">
          <Sparkles className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Estadísticas del Asesor IA</h2>
          <p className="text-sm text-muted-foreground">Feedback de respuestas del chatbot</p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filtros
            </CardTitle>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="h-8 text-xs">
                <X className="h-3 w-3 mr-1" />
                Limpiar
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="h-9"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Usuario</Label>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos los usuarios" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos los usuarios</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Tipo de Feedback</Label>
              <Select value={feedbackType} onValueChange={setFeedbackType}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="positive">Positivo</SelectItem>
                  <SelectItem value="negative">Negativo</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <MessageSquare className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Feedback</p>
                <p className="text-2xl font-bold">{summary.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-success/10">
                <ThumbsUp className="h-6 w-6 text-success" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Positivos</p>
                <p className="text-2xl font-bold">{summary.positive}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-destructive/10">
                <ThumbsDown className="h-6 w-6 text-destructive" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Negativos</p>
                <p className="text-2xl font-bold">{summary.negative}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-accent">
                <TrendingUp className="h-6 w-6 text-accent-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tasa Positiva</p>
                <p className="text-2xl font-bold">{summary.positiveRate.toFixed(0)}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Satisfaction Progress */}
      {summary.total > 0 && (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Satisfacción general</span>
                <span className="font-medium">{summary.positiveRate.toFixed(1)}%</span>
              </div>
              <Progress value={summary.positiveRate} className="h-3" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-1">
                  <ThumbsUp className="h-3 w-3 text-success" /> {summary.positive}
                </span>
                <span className="flex items-center gap-1">
                  {summary.negative} <ThumbsDown className="h-3 w-3 text-destructive" />
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Feedback Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            Feedback {hasActiveFilters ? 'Filtrado' : 'Reciente'}
            {hasActiveFilters && (
              <Badge variant="secondary" className="ml-2">
                {filteredFeedback.length} resultados
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredFeedback.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {hasActiveFilters ? 'No hay resultados para los filtros seleccionados' : 'No hay feedback registrado aún'}
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[100px]">Fecha</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Respuesta del IA</TableHead>
                    <TableHead className="w-[100px] text-center">Feedback</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredFeedback.slice(0, 50).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(item.created_at).toLocaleDateString('es-ES', {
                          day: '2-digit',
                          month: 'short',
                        })}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{item.user_name}</p>
                          <p className="text-xs text-muted-foreground">{item.user_email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-sm line-clamp-2 max-w-md">
                          {item.message_content}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.feedback === 'positive' ? (
                          <Badge className="bg-success/10 text-success border-0">
                            <ThumbsUp className="h-3 w-3 mr-1" />
                            Útil
                          </Badge>
                        ) : (
                          <Badge className="bg-destructive/10 text-destructive border-0">
                            <ThumbsDown className="h-3 w-3 mr-1" />
                            No útil
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
